import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, ChevronLeft, Mail, ShieldCheck, ShieldOff, RefreshCw, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Phase 3 — Named Pro-Shop Staff management.
 *
 * Lists everyone who can sign in to the back office for this club:
 *   - The shared-passcode `club_admin` identity (rendered as the
 *     "Counter Terminal" pseudo-account)
 *   - All `golf_shop_staff` named invitees
 *
 * Only the Club Admin (the shared-passcode identity) can invite, deactivate,
 * reactivate, or resend an invite. Named staff can view the page but the
 * action buttons are hidden.
 */
export default function AdminStaffPage() {
  const { user } = useAuth();
  const isClubAdmin = user?.role === "club_admin";
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", title: "", send_invitation: true });
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const reload = () => {
    setLoading(true);
    api
      .get("/admin/staff")
      .then((r) => setStaff(r.data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const invite = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/staff/invite", form);
      setFlash(`Invited ${data.name}${data.invitation_sent ? " — activation email sent" : ""}.`);
      setForm({ name: "", email: "", title: "", send_invitation: true });
      setShowInvite(false);
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const setStaffStatus = async (userId, deactivate) => {
    if (!window.confirm(deactivate ? "Deactivate this staff member? They will not be able to sign in." : "Reactivate this staff member?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/staff/${userId}/${deactivate ? "deactivate" : "reactivate"}`);
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resendInvite = async (userId) => {
    setBusy(true);
    try {
      await api.post(`/admin/staff/${userId}/resend-invite`);
      setFlash("Activation email re-sent.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen" data-testid="admin-staff-page">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#1A422D] hover:underline"
        >
          <ChevronLeft size={14} /> Back to Dashboard
        </Link>

        <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="overline">Pro Shop Team</div>
            <h1 className="font-serif text-4xl text-[#1A1A1A] mt-1">Staff</h1>
            <p className="text-[#4A4A4A] mt-2 max-w-2xl text-sm">
              Named accounts for every person on your team. They sign in at <code>/login</code> with their email and password — actions are attributed to them in the audit log.
              {!isClubAdmin && <> Only the Club Admin can invite or remove staff.</>}
            </p>
          </div>
          {isClubAdmin && (
            <button
              type="button"
              onClick={() => { setShowInvite(true); setFlash(null); }}
              data-testid="staff-invite-button"
              className="btn-primary inline-flex items-center gap-2"
            >
              <UserPlus size={16} /> Invite Staff
            </button>
          )}
        </div>

        {flash && (
          <div className="mt-4 px-4 py-3 text-sm border border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]" data-testid="staff-flash">{flash}</div>
        )}
        {error && (
          <div className="mt-4 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
        )}

        {showInvite && (
          <section className="mt-6 surface-card p-6 sm:p-8" data-testid="staff-invite-form">
            <div className="overline">New Staff</div>
            <h2 className="font-serif text-2xl mt-1">Invite Staff Member</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
              <Field label="Full name" value={form.name}
                     onChange={(v) => setForm({ ...form, name: v })}
                     placeholder="e.g. Lee Westwood" testId="staff-invite-name" />
              <Field label="Email" value={form.email}
                     onChange={(v) => setForm({ ...form, email: v })}
                     placeholder="lee@yourclub.com" testId="staff-invite-email" />
              <Field label="Title (optional)" value={form.title}
                     onChange={(v) => setForm({ ...form, title: v })}
                     placeholder="Head Pro / Assistant / Buyer"
                     testId="staff-invite-title" />
              <label className="text-xs text-[#1A1A1A] inline-flex items-center gap-2 sm:self-end sm:mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.send_invitation}
                  onChange={(e) => setForm({ ...form, send_invitation: e.target.checked })}
                  data-testid="staff-invite-send"
                  className="accent-[#1A422D]"
                />
                Email them a magic-link to set a password
              </label>
            </div>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button type="button" onClick={() => setShowInvite(false)} className="btn-secondary text-xs">Cancel</button>
              <button
                type="button"
                onClick={invite}
                disabled={busy || !form.name || !form.email}
                data-testid="staff-invite-save"
                className="btn-primary"
              >
                {busy ? "Inviting…" : "Send Invite"}
              </button>
            </div>
          </section>
        )}

        <div className="mt-8 surface-card overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F3F1E9]">
              <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-6 py-10 text-center text-[#7A7A7A]">Loading…</td></tr>}
              {!loading && staff.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#7A7A7A]">No staff yet.</td></tr>
              )}
              {staff.map((s) => {
                const shared = s.is_shared_passcode_identity;
                const active = s.status === "active";
                const isCurrentUser = user?.user_id === s.user_id;
                return (
                  <tr
                    key={s.user_id}
                    className="border-t border-[#E5E3D8]"
                    data-testid={`staff-row-${s.user_id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="font-serif text-lg text-[#1A1A1A]">{s.name}{isCurrentUser && <span className="ml-2 text-[10px] text-[#1A422D] uppercase tracking-[0.18em]">You</span>}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#1A422D] mt-0.5" data-testid={`staff-role-label-${s.user_id}`}>
                        {shared ? "Club Administrator" : "Club Staff"}
                      </div>
                      {s.title && <div className="text-xs text-[#7A7A7A] mt-0.5" data-testid={`staff-title-${s.user_id}`}>{s.title}</div>}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#4A4A4A] inline-flex items-center gap-1.5"><Mail size={12} /> {s.email}</td>
                    <td className="px-6 py-4 text-sm">
                      {shared ? (
                        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#8a6f33]">
                          <KeyRound size={12} /> Counter Terminal
                        </span>
                      ) : (
                        <span className="text-xs uppercase tracking-wider text-[#1A422D]">Named Staff</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1.5">
                        <span className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border w-fit ${
                          active
                            ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                            : s.status === "invited"
                              ? "border-[#B59A5A] bg-[#F8F1DE] text-[#8a6f33]"
                              : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
                        }`}>{active ? "Active" : (s.status === "invited" ? "Awaiting Activation" : "Deactivated")}</span>
                        {!shared && (
                          <span
                            data-testid={`staff-passcode-${s.user_id}`}
                            className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border w-fit ${
                              s.personal_passcode_set
                                ? "border-[#1A422D]/30 bg-[#E9F0EB] text-[#1A422D]"
                                : "border-[#B59A5A] bg-[#F8F1DE] text-[#8a6f33]"
                            }`}
                          >
                            <KeyRound size={10} className="inline mr-1 -mt-0.5" />
                            {s.personal_passcode_set ? "Personal passcode set" : "Awaiting passcode"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {isClubAdmin && !shared && (
                        <div className="inline-flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => resendInvite(s.user_id)}
                            disabled={busy}
                            data-testid={`staff-resend-${s.user_id}`}
                            className="text-xs text-[#1A422D] underline underline-offset-4 inline-flex items-center gap-1"
                          >
                            <RefreshCw size={12} /> {s.personal_passcode_set ? "Reset passcode" : "Resend invite"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setStaffStatus(s.user_id, active)}
                            disabled={busy}
                            data-testid={`staff-toggle-${s.user_id}`}
                            className="text-xs text-[#1A422D] underline underline-offset-4 inline-flex items-center gap-1"
                          >
                            {active ? <ShieldOff size={12} /> : <ShieldCheck size={12} />}
                            {active ? "Deactivate" : "Reactivate"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-xs text-[#7A7A7A]">
          Tip: the &ldquo;Counter Terminal&rdquo; row is your shared passcode &mdash; a break-glass identity anyone with the club passcode can use. Each named staff member should activate their own personal 6-12 digit passcode from the activation email, so every action they take in the back office is recorded against their name in the {""}
          <Link to="/admin/audit" className="underline text-[#1A422D]">audit log</Link>.
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="label-elegant">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="input-field w-full mt-1.5"
      />
    </div>
  );
}
